import type { Lot, LotEvent } from '../types/lot'
import { createId } from './id'
import {
  hasPendingLotOutboxChanges,
  lotApi,
  materializeLocalLotSnapshot,
  materializeLocalLotUpdate,
  queueMoveCommandProjection,
  queueTransferCommandProjection,
  queueMergeCommandProjection,
  queueSplitCommandProjection,
  type LotMutationResult
} from './lotApi'
import { buildMergeTargetLotDraft, buildSplitTargetLotDraft } from '../screens/lots/lineageCompatibility'

type LotLineageEvent = {
  id: string
  type: 'split' | 'merge' | 'move' | 'transfer'
  recordedAt: string
  sourceLotIds: string[]
  targetLotIds: string[]
  notes?: string
  fromFarmId?: string
  toFarmId?: string
  fromProductionUnitId?: string
  toProductionUnitId?: string
  fromLocationType?: 'production-unit' | 'farm' | 'processing-site'
  toLocationType?: 'production-unit' | 'farm' | 'processing-site'
  fromLocationId?: string
  toLocationId?: string
}

const uniqueStrings = (values: string[]): string[] => {
  const seen = new Set<string>()
  const out: string[] = []

  values.forEach((value) => {
    const key = String(value ?? '').trim()
    if (!key || seen.has(key)) return
    seen.add(key)
    out.push(key)
  })

  return out
}

const normalizeOptionalString = (value: string | undefined): string | undefined => {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : undefined
}

const compactRecord = (value: Record<string, unknown>): Record<string, unknown> | undefined => {
  const entries = Object.entries(value).filter(([, entry]) => entry !== undefined)
  if (entries.length === 0) {
    return undefined
  }
  return Object.fromEntries(entries)
}

const resolveLineageRootId = (lot: Lot): string | undefined => normalizeOptionalString(lot.lineageRootId) ?? normalizeOptionalString(lot.id)

const resolveSharedLineageRootId = (lots: Lot[]): string | undefined => {
  let sharedValue: string | undefined

  for (const lot of lots) {
    const nextValue = resolveLineageRootId(lot)
    if (!nextValue) return undefined

    if (sharedValue === undefined) {
      sharedValue = nextValue
      continue
    }

    if (sharedValue !== nextValue) {
      return undefined
    }
  }

  return sharedValue
}

const buildProjectedLedgerEvent = (event: LotLineageEvent, lineageRootId?: string): LotEvent => ({
  id: event.id,
  eventType: event.type,
  recordedAt: event.recordedAt,
  lineageRootId: normalizeOptionalString(lineageRootId),
  sourceLotIds: uniqueStrings(event.sourceLotIds),
  targetLotIds: uniqueStrings(event.targetLotIds),
  fromLocationType: event.fromLocationType,
  fromLocationId: normalizeOptionalString(event.fromLocationId),
  toLocationType: event.toLocationType,
  toLocationId: normalizeOptionalString(event.toLocationId),
  notes: normalizeOptionalString(event.notes),
  metadata: compactRecord({
    fromFarmId: normalizeOptionalString(event.fromFarmId),
    toFarmId: normalizeOptionalString(event.toFarmId),
    fromProductionUnitId: normalizeOptionalString(event.fromProductionUnitId),
    toProductionUnitId: normalizeOptionalString(event.toProductionUnitId)
  })
})

export type RecordLotSplitArgs = {
  sourceLot: Lot
  childLotKeys: string[]
  recordedAt: string
  notes?: string
}

export type RecordLotSplitResult = {
  createdLots: Lot[]
  updatedSourceLot: Lot
}

export type RecordLotMoveArgs = {
  sourceLot: Lot
  targetProductionUnitId?: string
  targetLocationType?: Extract<Lot['locationType'], 'production-unit' | 'processing-site'>
  targetLocationId?: string
  recordedAt: string
  notes?: string
}

export type RecordLotTransferArgs = {
  sourceLot: Lot
  targetFarmId: string
  targetProductionUnitId?: string
  targetLocationType?: Exclude<Lot['locationType'], undefined>
  targetLocationId?: string
  recordedAt: string
  notes?: string
}

const resolveTransferDestination = (args: RecordLotTransferArgs) => {
  const targetFarmId = args.targetFarmId.trim()
  const targetProductionUnitId = normalizeOptionalString(args.targetProductionUnitId)
  const targetLocationType = args.targetLocationType ?? 'production-unit'

  if (!targetFarmId) {
    throw new Error('Target facility is required')
  }

  const explicitLocationId = normalizeOptionalString(args.targetLocationId)
  if (targetLocationType === 'farm') {
    const targetLocationId = explicitLocationId ?? targetFarmId
    if (targetLocationId !== targetFarmId) {
      throw new Error('Farm transfer destination must use the target facility id as locationId')
    }

    return {
      targetFarmId,
      targetProductionUnitId,
      targetLocationType,
      targetLocationId
    }
  }

  if (targetLocationType === 'processing-site') {
    if (!explicitLocationId) {
      throw new Error('Processing-site transfer destination must provide explicit locationId')
    }

    return {
      targetFarmId,
      targetProductionUnitId,
      targetLocationType,
      targetLocationId: explicitLocationId
    }
  }

  if (!targetProductionUnitId) {
    throw new Error('Target production unit is required')
  }

  const targetLocationId = explicitLocationId ?? targetProductionUnitId
  if (targetLocationId !== targetProductionUnitId) {
    throw new Error('Production-unit transfer destination must use the target production unit id as locationId')
  }

  return {
    targetFarmId,
    targetProductionUnitId,
    targetLocationType,
    targetLocationId
  }
}

const canUseRemoteLineageCommand = async (): Promise<boolean> => {
  if (typeof navigator === 'undefined' || !navigator.onLine) {
    return false
  }

  return !(await hasPendingLotOutboxChanges())
}

export const recordLotMove = async (args: RecordLotMoveArgs): Promise<LotMutationResult<Lot>> => {
  const targetLocationType = args.targetLocationType ?? 'production-unit'
  const targetProductionUnitId = normalizeOptionalString(args.targetProductionUnitId)
  const targetLocationId = normalizeOptionalString(args.targetLocationId)

  if (targetLocationType === 'processing-site') {
    if (!targetLocationId) {
      throw new Error('Target processing site is required')
    }
  } else if (!targetProductionUnitId) {
    throw new Error('Target production unit is required')
  }

  const resolvedTargetLocationId = targetLocationType === 'processing-site'
    ? targetLocationId!
    : targetProductionUnitId!

  if (
    args.sourceLot.locationType === targetLocationType &&
    (args.sourceLot.locationId ?? args.sourceLot.productionUnitId ?? '') === resolvedTargetLocationId
  ) {
    return {
      data: args.sourceLot,
      syncState: 'synced',
      syncPromise: null
    }
  }

  if (await canUseRemoteLineageCommand()) {
    return lotApi.moveRemote(args.sourceLot.id, {
      targetProductionUnitId,
      targetLocationType: targetLocationType === 'processing-site' ? targetLocationType : undefined,
      targetLocationId: targetLocationType === 'processing-site' ? resolvedTargetLocationId : undefined,
      recordedAt: args.recordedAt,
      notes: args.notes?.trim() || undefined
    })
  }

  const eventId = createId('lineage')
  const event: LotLineageEvent = {
    id: eventId,
    type: 'move',
    recordedAt: args.recordedAt,
    sourceLotIds: [args.sourceLot.id],
    targetLotIds: [args.sourceLot.id],
    notes: args.notes?.trim() || undefined,
    fromFarmId: args.sourceLot.farmId,
    toFarmId: args.sourceLot.farmId,
    fromProductionUnitId: args.sourceLot.productionUnitId,
    toProductionUnitId: targetProductionUnitId,
    fromLocationType: args.sourceLot.locationType,
    toLocationType: targetLocationType,
    fromLocationId: args.sourceLot.locationId,
    toLocationId: resolvedTargetLocationId
  }

  const movedLot = materializeLocalLotUpdate(args.sourceLot, {
    productionUnitId: targetProductionUnitId,
    locationType: targetLocationType,
    locationId: resolvedTargetLocationId
  })
  const projectedEvent = buildProjectedLedgerEvent(event, resolveLineageRootId(args.sourceLot))

  return queueMoveCommandProjection({
    payload: {
      sourceLotId: args.sourceLot.id,
      targetProductionUnitId,
      targetLocationType: targetLocationType === 'processing-site' ? targetLocationType : undefined,
      targetLocationId: targetLocationType === 'processing-site' ? resolvedTargetLocationId : undefined,
      eventId,
      recordedAt: args.recordedAt,
      notes: event.notes
    },
    movedLot,
    events: [projectedEvent]
  })
}

export const recordLotTransfer = async (args: RecordLotTransferArgs): Promise<LotMutationResult<Lot>> => {
  const {
    targetFarmId,
    targetProductionUnitId,
    targetLocationType,
    targetLocationId
  } = resolveTransferDestination(args)
  if (targetFarmId === args.sourceLot.farmId) {
    throw new Error('Use move for same-facility location changes.')
  }

  if (await canUseRemoteLineageCommand()) {
    return lotApi.transferRemote(args.sourceLot.id, {
      targetFarmId,
      targetProductionUnitId,
      targetLocationType,
      targetLocationId,
      recordedAt: args.recordedAt,
      notes: args.notes?.trim() || undefined
    })
  }

  const eventId = createId('lineage')
  const event: LotLineageEvent = {
    id: eventId,
    type: 'transfer',
    recordedAt: args.recordedAt,
    sourceLotIds: [args.sourceLot.id],
    targetLotIds: [args.sourceLot.id],
    notes: args.notes?.trim() || undefined,
    fromFarmId: args.sourceLot.farmId,
    toFarmId: targetFarmId,
    fromProductionUnitId: args.sourceLot.productionUnitId,
    toProductionUnitId: targetProductionUnitId,
    fromLocationType: args.sourceLot.locationType,
    toLocationType: targetLocationType,
    fromLocationId: args.sourceLot.locationId,
    toLocationId: targetLocationId
  }

  const transferredLot = materializeLocalLotUpdate(args.sourceLot, {
    farmId: targetFarmId,
    productionUnitId: targetProductionUnitId,
    locationType: targetLocationType,
    locationId: targetLocationId
  })
  const projectedEvent = buildProjectedLedgerEvent(event, resolveLineageRootId(args.sourceLot))

  return queueTransferCommandProjection({
    payload: {
      sourceLotId: args.sourceLot.id,
      targetFarmId,
      targetProductionUnitId,
      targetLocationType,
      targetLocationId,
      eventId,
      recordedAt: args.recordedAt,
      notes: event.notes
    },
    transferredLot,
    events: [projectedEvent]
  })
}

export const recordLotSplit = async (args: RecordLotSplitArgs): Promise<RecordLotSplitResult> => {
  if (await canUseRemoteLineageCommand()) {
    return lotApi.splitRemote(args.sourceLot.id, {
      childLotKeys: args.childLotKeys,
      recordedAt: args.recordedAt,
      notes: args.notes?.trim() || undefined
    })
  }

  const targetLotIds = args.childLotKeys.map(() => createId('lot'))
  const eventId = createId('lineage')
  const event: LotLineageEvent = {
    id: eventId,
    type: 'split',
    recordedAt: args.recordedAt,
    sourceLotIds: [args.sourceLot.id],
    targetLotIds,
    notes: args.notes?.trim() || undefined
  }

  const createdLots = args.childLotKeys.map((lotKey, index) => {
    const draft = buildSplitTargetLotDraft(args.sourceLot, lotKey)
    return materializeLocalLotSnapshot({
      id: targetLotIds[index],
      ...draft,
      stage: 'Active',
      stockingDate: undefined,
      harvestDate: undefined,
      biomassKg: undefined
    })
  })

  const updatedSourceLot = materializeLocalLotUpdate(args.sourceLot, {})
  const projectedEvent = buildProjectedLedgerEvent(event, resolveLineageRootId(args.sourceLot))

  await queueSplitCommandProjection({
    payload: {
      sourceLotId: args.sourceLot.id,
      childLotKeys: args.childLotKeys,
      childLotIds: targetLotIds,
      eventId,
      recordedAt: args.recordedAt,
      notes: event.notes
    },
    createdLots,
    updatedSourceLot,
    events: [projectedEvent]
  })

  return {
    createdLots,
    updatedSourceLot
  }
}

export type RecordLotMergeArgs = {
  sourceLots: Lot[]
  targetLotKey: string
  recordedAt: string
  notes?: string
  markSourceLotsInactive?: boolean
}

export type RecordLotMergeResult = {
  targetLot: Lot
  updatedSourceLots: Lot[]
}

export const recordLotMerge = async (args: RecordLotMergeArgs): Promise<RecordLotMergeResult> => {
  if (await canUseRemoteLineageCommand()) {
    return lotApi.mergeRemote({
      sourceLotIds: args.sourceLots.map((lot) => lot.id),
      targetLotKey: args.targetLotKey,
      recordedAt: args.recordedAt,
      notes: args.notes?.trim() || undefined,
      markSourceLotsInactive: args.markSourceLotsInactive
    })
  }

  const targetLotId = createId('lot')
  const eventId = createId('lineage')
  const event: LotLineageEvent = {
    id: eventId,
    type: 'merge',
    recordedAt: args.recordedAt,
    sourceLotIds: args.sourceLots.map((lot) => lot.id),
    targetLotIds: [targetLotId],
    notes: args.notes?.trim() || undefined
  }

  const targetDraft = buildMergeTargetLotDraft(args.sourceLots, args.targetLotKey)
  const targetLot = materializeLocalLotSnapshot({
    id: targetLotId,
    ...targetDraft,
    stage: 'Active',
    stockingDate: undefined,
    harvestDate: undefined,
    biomassKg: undefined
  })

  const updatedSourceLots = args.sourceLots.map((sourceLot) => materializeLocalLotUpdate(sourceLot, {
    stage: args.markSourceLotsInactive ? 'Inactive' : sourceLot.stage
  }))
  const projectedEvent = buildProjectedLedgerEvent(event, resolveSharedLineageRootId(args.sourceLots))

  await queueMergeCommandProjection({
    payload: {
      sourceLotIds: args.sourceLots.map((lot) => lot.id),
      targetLotKey: args.targetLotKey,
      targetLotId,
      eventId,
      recordedAt: args.recordedAt,
      notes: event.notes,
      markSourceLotsInactive: args.markSourceLotsInactive
    },
    targetLot,
    updatedSourceLots,
    events: [projectedEvent]
  })

  return {
    targetLot,
    updatedSourceLots
  }
}